/** Копилки — деньги, отложенные под конкретную будущую трату.
 *
 * Отличие от целей здесь главное и его видно сразу: в цель ничего не кладут,
 * она меряется общим накопленным; в копилку кладут. Виза, страховка, билеты,
 * налоговый резерв — деньги там уже лежат и на другое не считаются, поэтому у
 * каждой строки своя сумма и кнопка «Пополнить», а не доля от общего пула.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type Failure, type Fund, type Funds } from "../lib/api.ts";
import { dayLabel } from "../lib/group.ts";
import { formatMoney } from "../lib/money.ts";
import { plural } from "../lib/plural.ts";
import { tap } from "../lib/telegram.ts";
import { useOnce } from "../lib/once.ts";
import { Header } from "../components/Header.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { Fallback } from "./Fallback.tsx";
import { AddButton, SheetRow, card, hint } from "../components/ui.tsx";

export function FundsScreen({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<Funds | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [opened, setOpened] = useState<Fund | null>(null);

  const load = () => {
    setLoading(true);
    setFailure(null);
    api.funds().then(setData)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Копилки" onBack={onBack}
              action={<AddButton label="Новая копилка" onPick={() => setAdding(true)} />} />

      {loading || !data ? <Skeleton /> : data.items.length === 0 ? (
        <Empty onAdd={() => setAdding(true)} />
      ) : (
        <section style={{ ...card, padding: "4px var(--s-5)" }}>
          {data.items.map((fund, i) => (
            <FundRow key={fund.id} fund={fund} last={i === data.items.length - 1}
                     onOpen={() => setOpened(fund)} />
          ))}
        </section>
      )}

      {adding && <AddSheet onClose={() => setAdding(false)}
                           onDone={() => { setAdding(false); load(); }} />}
      {opened && <FundSheet fund={opened} onClose={() => setOpened(null)}
                            onDone={() => { setOpened(null); load(); }} />}
    </div>
  );
}

function FundRow({ fund, last, onOpen }: {
  fund: Fund; last: boolean; onOpen: () => void;
}) {
  // Ширина полоски — пиксели, а не деньги: единственное место, где сумму
  // разрешено превращать в число (MA-INV-6).
  const filled = Math.min(Number(fund.saved) / Math.max(Number(fund.target), 1e-9), 1) * 100;
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }}
      style={{ display: "block", width: "100%", padding: "14px 0", background: "none",
               border: "none", font: "inherit", color: "inherit", cursor: "pointer",
               textAlign: "left",
               borderBottom: last ? "none" : "1px solid var(--separator)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
                    gap: "var(--s-3)" }}>
        <span style={{ fontSize: "var(--f-body)", fontWeight: 500, minWidth: 0,
                       overflow: "hidden", textOverflow: "ellipsis",
                       whiteSpace: "nowrap" }}>{fund.name}</span>
        <span className="num" style={{ fontSize: "var(--f-body)", fontWeight: 600,
                                       flexShrink: 0 }}>
          {formatMoney(fund.saved, fund.currency)}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--surface-3)",
                    marginTop: 9, overflow: "hidden" }}>
        <div style={{ width: `${filled}%`, height: "100%",
                      background: fund.is_full ? "var(--income)" : "var(--data)" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7,
                    gap: "var(--s-3)" }}>
        <span className="num" style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>
          {fund.is_full
            ? "Собрано"
            : `из ${formatMoney(fund.target, fund.currency)} · ${monthsLabel(fund.months_left)}`}
        </span>
        {!fund.is_full && (
          <span className="num" style={{ fontSize: "var(--f-caption)",
                                         color: "var(--text-muted)", flexShrink: 0 }}>
            {formatMoney(fund.required_monthly, fund.currency)} в месяц
          </span>
        )}
      </div>
    </button>
  );
}

/** «через 6 месяцев» / «срок уже пришёл». Ноль месяцев — не ошибка и не ноль
 *  денег: это значит «вносить нужно всё сейчас», и так и надо сказать. */
function monthsLabel(months: number): string {
  return months <= 0
    ? "срок уже пришёл"
    : plural(months, ["месяц", "месяца", "месяцев"]);
}

function AddSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");
  const once = useOnce();

  const ready = name.trim() && target.trim() && due;

  const submit = () => {
    if (busy) return;
    tap();
    setBusy(true);
    setTrouble("");
    once(() => api.addFund({ name: name.trim(), target: target.trim(), currency,
                             due_date: due })
      .then(onDone)
      .catch(() => setTrouble("Не получилось сохранить. Проверь сумму и дату."))
      .finally(() => setBusy(false)));
  };

  return (
    <Sheet title="Новая копилка" onClose={onClose}>
      <p style={{ ...hint, marginTop: 0, marginBottom: "var(--s-4)" }}>
        Копилка — под конкретную будущую трату. Валюта копилки — валюта этой траты:
        виза стоит 1200 $, а не «примерно столько в рублях».
      </p>
      <Field label="На что">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
               placeholder="Виза на год" aria-label="На что" style={input} />
      </Field>
      <Field label="Сколько нужно">
        <div style={{ display: "flex", gap: "var(--s-2)" }}>
          <input value={target} onChange={(e) => setTarget(e.target.value)} inputMode="decimal"
                 placeholder="1200" aria-label="Сколько нужно" style={{ ...input, flexGrow: 1 }} />
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                  aria-label="Валюта копилки"
                  style={{ ...input, width: "auto", padding: "0 12px" }}>
            <option value="USD">$</option>
            <option value="VND">₫</option>
            <option value="RUB">₽</option>
          </select>
        </div>
      </Field>
      <Field label="К какому сроку">
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
               aria-label="К какому сроку" style={input} />
      </Field>
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <button type="button" disabled={!ready || busy} onClick={submit}
              style={{ ...primary, marginTop: "var(--s-4)",
                       background: ready && !busy ? "var(--accent)" : "var(--surface-2)",
                       color: ready && !busy ? "var(--on-accent)" : "var(--text-disabled)" }}>
        Завести
      </button>
    </Sheet>
  );
}

function FundSheet({ fund, onClose, onDone }: {
  fund: Fund; onClose: () => void; onDone: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Принимает функцию, а не промис: `run(api.contribute(...))` отправлял бы
  // запрос ещё при вычислении аргумента — до того, как защита успеет сработать.
  const once = useOnce();

  const run = (call: () => Promise<unknown>, oops: string) => {
    tap();
    setTrouble("");
    once(() => {
      setBusy(true);
      return call().then(onDone).catch(() => setTrouble(oops))
        .finally(() => setBusy(false));
    });
  };

  return (
    <Sheet title={fund.name} onClose={onClose}>
      <div style={{ textAlign: "center", marginBottom: "var(--s-5)" }}>
        <div className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                      letterSpacing: "-0.8px" }}>
          {formatMoney(fund.saved, fund.currency)}
        </div>
        <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", marginTop: 4 }}>
          {fund.is_full
            ? "собрано полностью"
            : `из ${formatMoney(fund.target, fund.currency)}`}
        </div>
      </div>

      <div style={{ background: "var(--surface)", borderRadius: "var(--r-card)",
                    padding: "2px var(--s-4)" }}>
        <SheetRow label="Срок" value={dayLabel(fund.due_date)} />
        <SheetRow label="Осталось месяцев" value={String(Math.max(fund.months_left, 0))} />
        <SheetRow label="Нужно в месяц"
             value={formatMoney(fund.required_monthly, fund.currency)} last />
      </div>

      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    margin: "var(--s-5) 0 var(--s-2)" }}>
        Взнос — со знаком минус, если достаёшь обратно
      </div>
      <div style={{ display: "flex", gap: "var(--s-2)" }}>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
               placeholder="100" aria-label="Взнос" style={{ ...input, flexGrow: 1 }} />
        {/* aria-disabled, а не disabled: кнопка остаётся на экране, пока идёт
            запрос, и `disabled` в этот момент роняет фокус в body — ровно тогда,
            когда человек ждёт ответа на своё нажатие. Браузер такую кнопку не
            блокирует, поэтому нажатие отбивает `run`. */}
        <button type="button" aria-disabled={busy || !amount.trim() || undefined}
                onClick={() => amount.trim() && run(
                  () => api.contribute(fund.id, amount.trim()).then(() => setAmount("")),
                  "Не получилось. Проверь сумму.")}
                style={{ minHeight: "var(--tap)", padding: "0 18px", border: "none",
                         borderRadius: "var(--r-field)", cursor: "pointer", font: "inherit",
                         fontSize: "var(--f-body)", fontWeight: 600,
                         background: "var(--accent)", color: "var(--on-accent)" }}>
          Внести
        </button>
      </div>

      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}

      {/* Удаление копилки — не «отменить операцию»: тут не запись о прошлом, а
          план на будущее, и вернуть его тостом нельзя. Поэтому подтверждение. */}
      {confirming ? (
        <div style={{ display: "flex", gap: "var(--s-2)", marginTop: "var(--s-5)" }}>
          <button type="button" aria-disabled={busy || undefined}
                  onClick={() => run(() => api.dropFund(fund.id), "Не получилось удалить копилку. Попробуй ещё раз.")}
                  style={{ ...primary, flexGrow: 1, background: "var(--danger)",
                           color: "var(--on-accent)" }}>
            Удалить копилку
          </button>
          <button type="button" onClick={() => setConfirming(false)}
                  style={{ ...primary, flexGrow: 1, background: "var(--surface-2)",
                           color: "var(--text)" }}>
            Оставить
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => { tap(); setConfirming(true); }}
                style={{ background: "none", border: "none", cursor: "pointer", font: "inherit",
                         fontSize: "var(--f-caption)", color: "var(--danger)",
                         padding: "14px 0", minHeight: 44, marginTop: "var(--s-3)" }}>
          Удалить копилку
        </button>
      )}
    </Sheet>
  );
}


function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: "var(--s-4)" }}>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    marginBottom: "var(--s-2)" }}>{label}</div>
      {children}
    </div>
  );
}

function Empty({ onAdd }: { onAdd: () => void }) {
  return (
    <section style={{ ...card, textAlign: "center" }}>
      <p style={{ fontSize: "var(--f-body)", lineHeight: 1.6, margin: 0 }}>
        Копилок пока нет.
      </p>
      <p style={{ ...hint, marginBottom: "var(--s-4)" }}>
        Копилка нужна там, где крупная трата известна заранее: виза, страховка, билеты,
        налог. Приложение посчитает, сколько откладывать в месяц, чтобы успеть к сроку.
      </p>
      <button type="button" onClick={() => { tap(); onAdd(); }} style={primary}>
        Завести копилку
      </button>
    </section>
  );
}

function Skeleton() {
  return (
    <div style={card} role="status" aria-busy="true" aria-label="Загружаю копилки">
      <div style={{ height: 11, width: "42%", background: "var(--surface-3)", borderRadius: 5 }} />
      <div style={{ height: 6, background: "var(--surface-2)", borderRadius: 3,
                    marginTop: "var(--s-4)" }} />
    </div>
  );
}

const input: React.CSSProperties = {
  width: "100%", minHeight: "var(--tap)", background: "var(--surface-2)", border: "none",
  borderRadius: "var(--r-field)", padding: "12px 14px", color: "var(--text)",
  font: "inherit", fontSize: "var(--f-body)",
};
const primary: React.CSSProperties = {
  width: "100%", minHeight: "var(--tap)", padding: 14, borderRadius: "var(--r-field)",
  border: "none", cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
  fontWeight: 600, background: "var(--accent)", color: "var(--on-accent)",
};
